<template>
  <div class="history-log">
    <header class="page-header">
      <div>
        <h2>历史记录</h2>
        <p class="subtitle">追踪课程、报名与黑名单的操作轨迹</p>
      </div>
    </header>

    <section class="filter-panel">
      <div class="filter-item">
        <label>实体类型</label>
        <select v-model="filters.entityType">
          <option value="">全部</option>
          <option v-for="option in entityOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>
      <div class="filter-actions">
        <button class="primary-button" @click="loadLogs">查询</button>
        <button class="ghost-button" @click="resetFilters">重置</button>
      </div>
    </section>

    <section class="table-wrapper" v-if="logs.length">
      <table>
        <thead>
          <tr>
            <th>时间</th>
            <th>实体类型</th>
            <th>实体ID</th>
            <th>操作</th>
            <th>详情</th>
            <th>操作人</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="log in logs" :key="log.id">
            <td>{{ formatDate(log.createdAt) }}</td>
            <td>{{ entityLabel(log.entityType) }}</td>
            <td>{{ log.entityId }}</td>
            <td><span class="action-tag">{{ log.action }}</span></td>
            <td>{{ log.detail || '—' }}</td>
            <td>{{ log.operatorName || '系统' }}</td>
          </tr>
        </tbody>
      </table>
      <div class="pagination">
        <button @click="changePage(pagination.page - 1)" :disabled="pagination.page === 0 || loading">上一页</button>
        <span>第 {{ pagination.page + 1 }} / {{ totalPages }} 页</span>
        <button @click="changePage(pagination.page + 1)" :disabled="pagination.page + 1 >= totalPages || loading">
          下一页
        </button>
      </div>
    </section>

    <div v-else class="empty-state">
      <p>暂无历史记录</p>
    </div>
  </div>
</template>

<script>
import { historyService } from '@/services/historyService'

const ENTITY_LABELS = {
  ENROLLMENT: '报名',
  BLACKLIST: '黑名单',
  COURSE: '课程'
}

export default {
  name: 'HistoryLog',
  data() {
    return {
      logs: [],
      loading: false,
      filters: {
        entityType: ''
      },
      pagination: {
        page: 0,
        size: 10,
        total: 0
      },
      entityOptions: Object.entries(ENTITY_LABELS).map(([value, label]) => ({ value, label }))
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.pagination.total / this.pagination.size) || 1
    }
  },
  created() {
    this.loadLogs()
  },
  methods: {
    formatDate(value) {
      if (!value) return '—'
      return value.replace('T', ' ').replace('Z', '')
    },
    entityLabel(code) {
      return ENTITY_LABELS[code] || code || '—'
    },
    async loadLogs() {
      try {
        this.loading = true
        const params = {
          page: this.pagination.page,
          size: this.pagination.size,
          entityType: this.filters.entityType || undefined
        }
        const response = await historyService.fetchHistory(params)
        if (response.success) {
          this.logs = response.data.content || []
          this.pagination.total = response.data.totalElements || 0
        }
      } catch (error) {
        console.error('加载历史记录失败', error)
      } finally {
        this.loading = false
      }
    },
    resetFilters() {
      this.filters.entityType = ''
      this.pagination.page = 0
      this.loadLogs()
    },
    changePage(page) {
      if (page < 0) return
      if (page >= this.totalPages) return
      this.pagination.page = page
      this.loadLogs()
    }
  }
}
</script>

<style scoped>
.history-log {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.subtitle {
  color: #6b7280;
  margin-top: 0.25rem;
}

.filter-panel {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  background: #fff;
  padding: 1rem;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-item label {
  font-size: 0.85rem;
  color: #4b5563;
}

.filter-item select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.filter-actions {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
}

.table-wrapper {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead th {
  background: #f9fafb;
  text-align: left;
  padding: 0.75rem 1rem;
  color: #6b7280;
  font-weight: 600;
  font-size: 0.9rem;
}

tbody td {
  padding: 0.8rem 1rem;
  border-top: 1px solid #f1f5f9;
  vertical-align: top;
  text-align: left;
}

.action-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  background: rgba(59, 130, 246, 0.1);
  color: #2563eb;
}

.primary-button,
.ghost-button {
  cursor: pointer;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  transition: all 0.2s ease;
}

.primary-button {
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  color: #fff;
  padding: 0.55rem 1.2rem;
  box-shadow: 0 10px 20px rgba(37, 99, 235, 0.25);
}

.ghost-button {
  background: #f3f4f6;
  color: #374151;
  padding: 0.55rem 1.2rem;
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid #f1f5f9;
  background: #fafafa;
}

.empty-state {
  text-align: center;
  color: #6b7280;
  padding: 2rem;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}
</style>

